import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { ExternalLink, Copy, Check, Loader2, ShieldCheck, ArrowDownCircle, Mailbox, Send } from "lucide-react";

const ENTRA_ROLES_URL = "https://entra.microsoft.com/#view/Microsoft_AAD_IAM/RolesManagementMenuBlade/~/AllRoles";

// Guided least-privilege Exchange flow: escalate to Exchange Administrator → verify the app-policy
// cmdlets are available → downgrade to the least-privilege Exchange Recipient Administrator → verify
// recipient management still works. Renders inside Setup Wizard step 4.
export function ExoLeastPrivilege({ appId, onStatus }) {
  const { t } = useLang();
  const [phase, setPhase] = useState("escalate"); // escalate | downgrade | complete
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [probing, setProbing] = useState(false);
  const seenAdminRef = useRef(false);

  // Scoped Mail.Send policy — created WHILE Exchange Administrator is still assigned (downgrade phase).
  const [scope, setScope] = useState(null); // {mailboxes, groups} | "loading" | "error"
  const [selMbx, setSelMbx] = useState("");
  const [selGrp, setSelGrp] = useState("");
  const [policyBusy, setPolicyBusy] = useState(false);
  const [policyDone, setPolicyDone] = useState(false);
  const [testBusy, setTestBusy] = useState(false);
  const [testSent, setTestSent] = useState(false);
  const [existingPolicy, setExistingPolicy] = useState(null); // {scopeName, scopeSmtp} | null
  const scopeFetchedRef = useRef(false);
  const prevPhaseRef = useRef("escalate");

  const apply = (d) => {
    setStatus(d);
    onStatus && onStatus(d);
    let next;
    if (d.appPolicyCmdlets || d.exchangeAdminRole === true) { seenAdminRef.current = true; next = "downgrade"; }
    else if ((seenAdminRef.current || d.leastPrivilegeDone) && d.recipientAccess) next = "complete";
    else next = "escalate";
    if (next === "complete" && prevPhaseRef.current !== "complete") {
      toast.success(t("wizard.lpCompleteToast"));
    }
    prevPhaseRef.current = next;
    setPhase(next);
  };

  const fetchStatus = async () => {
    setProbing(true);
    try {
      const { data } = await api.get("/settings/exo/least-privilege-status");
      apply(data);
      return data;
    } catch { return null; }
    finally { setProbing(false); }
  };

  // Auto-poll while not complete.
  useEffect(() => {
    let cancelled = false;
    (async () => { const d = await fetchStatus(); if (cancelled) return; })();
    const id = setInterval(async () => {
      if (phase === "complete") return;
      await fetchStatus();
    }, 12000);
    return () => { cancelled = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  const verify = async () => {
    setBusy(true);
    try {
      const d = await fetchStatus();
      if (!d) { toast.error(t("wizard.lpVerifyFailed")); return; }
      if (d.appPolicyCmdlets || d.exchangeAdminRole === true) toast.success(t("wizard.lpEscalateOk"));
      else if (d.recipientAccess && (seenAdminRef.current || d.leastPrivilegeDone)) toast.success(t("wizard.lpRecipientOk"));
      else toast.message(t("wizard.lpVerifyFailed"));
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  const copyAppId = async () => {
    try { await navigator.clipboard.writeText(appId || ""); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* no clipboard */ }
  };

  // On reaching the downgrade phase (Exchange Administrator confirmed), load the mailbox + security-group
  // options so the admin can create the scoped Mail.Send policy before downgrading.
  useEffect(() => {
    if (phase !== "downgrade" || scopeFetchedRef.current) return;
    scopeFetchedRef.current = true;
    setScope("loading");
    Promise.all([
      api.get("/settings/exo/shared-mailboxes").then((r) => r.data?.mailboxes || []).catch(() => []),
      api.get("/settings/exo/security-groups").then((r) => r.data?.groups || []).catch(() => []),
      api.get("/settings/exo/send-policy").then((r) => r.data).catch(() => null),
    ]).then(([mailboxes, groups, policy]) => {
      setScope({ mailboxes, groups });
      if (mailboxes[0]) setSelMbx(mailboxes[0].PrimarySmtpAddress || "");
      if (policy?.exists) {
        // A scoped send policy already exists — treat as done and pre-select its group.
        setExistingPolicy(policy);
        setPolicyDone(true);
        const match = groups.find((g) => g.PrimarySmtpAddress === policy.scopeSmtp || g.DisplayName === policy.scopeName);
        setSelGrp((match || groups[0])?.PrimarySmtpAddress || "");
      } else if (groups[0]) {
        setSelGrp(groups[0].PrimarySmtpAddress || "");
      }
    }).catch(() => setScope("error"));
  }, [phase]);

  const createPolicy = async () => {
    if (!selMbx || !selGrp) { toast.error(t("wizard.lpPolicyNeedBoth")); return; }
    setPolicyBusy(true);
    try {
      const { data } = await api.post("/settings/exo/prepare-notify-policy", { mailbox: selMbx, group: selGrp });
      setPolicyDone(true);
      toast.success(data?.message || t("wizard.lpPolicyCreated"));
    } catch (e) { toast.error(apiErr(e)); }
    finally { setPolicyBusy(false); }
  };

  const sendTestEmail = async () => {
    setTestBusy(true);
    try {
      const { data } = await api.post("/settings/exo/send-test-email", {});
      setTestSent(true);
      toast.success(data?.message || t("wizard.lpPolicyTestSent"));
    } catch (e) { toast.error(apiErr(e)); }
    finally { setTestBusy(false); }
  };

  const phaseLabel = phase === "escalate" ? "wizard.lpPhaseEscalate"
    : phase === "downgrade" ? "wizard.lpPhaseDowngrade" : "wizard.lpPhaseComplete";

  const escalateSteps = ["wizard.lpEscalateStep1", "wizard.lpEscalateStep2", "wizard.lpEscalateStep3"];
  const downgradeSteps = ["wizard.lpDowngradeStep1", "wizard.lpDowngradeStep2", "wizard.lpDowngradeStep3"];
  const steps = phase === "escalate" ? escalateSteps : phase === "downgrade" ? downgradeSteps : [];

  return (
    <div className="space-y-3" data-testid="exo-least-privilege">
      <p className="text-sm text-zinc-500">{t("wizard.lpIntro")}</p>

      {(() => {
        const activity = policyBusy ? t("wizard.lpBusyPolicy")
          : testBusy ? t("wizard.lpBusyTest")
          : scope === "loading" ? t("wizard.lpBusyLoading")
          : (busy || probing) ? t("wizard.lpBusyConnecting")
          : null;
        return activity ? (
          <div className="flex items-center gap-2 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs font-medium text-sky-800" data-testid="lp-activity">
            <Loader2 size={14} className="shrink-0 animate-spin" /> {activity}
          </div>
        ) : null;
      })()}

      {/* Phase progress rail */}
      <div className="flex items-center gap-2" data-testid="lp-phase-rail">
        {["escalate", "downgrade", "complete"].map((p, i) => {
          const order = { escalate: 0, downgrade: 1, complete: 2 };
          const done = order[phase] > i;
          const active = phase === p;
          return (
            <div key={p} className="flex flex-1 items-center gap-2">
              <div className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${done ? "bg-emerald-600 text-white" : active ? "bg-zinc-900 text-white" : "bg-zinc-200 text-zinc-500"}`}>
                {done ? <Check size={12} /> : i + 1}
              </div>
              {i < 2 && <div className={`h-px flex-1 ${done ? "bg-emerald-500" : "bg-zinc-200"}`} />}
            </div>
          );
        })}
      </div>

      {/* Status banner */}
      <div
        data-testid="lp-status-banner"
        className={`flex items-center gap-2 rounded-lg border p-3 text-sm font-medium ${
          phase === "complete" ? "border-emerald-200 bg-emerald-50 text-emerald-800"
          : phase === "downgrade" ? "border-sky-200 bg-sky-50 text-sky-800"
          : "border-amber-200 bg-amber-50 text-amber-800"}`}>
        {phase === "complete" ? <ShieldCheck size={16} className="shrink-0" />
          : phase === "downgrade" ? <ArrowDownCircle size={16} className="shrink-0" />
          : <Loader2 size={16} className="shrink-0 animate-spin" />}
        <span>
          <span className="font-bold">{t(phaseLabel)}</span>
          {" — "}
          {phase === "escalate" ? t("wizard.lpEscalatePending")
            : phase === "downgrade" ? t("wizard.lpEscalateOk")
            : t("wizard.lpCompleteDesc")}
        </span>
      </div>

      {phase !== "complete" && (
        <>
          <p className="text-sm text-zinc-600" data-testid="lp-phase-desc">
            {phase === "escalate" ? t("wizard.lpEscalateDesc") : t("wizard.lpDowngradeDesc")}
          </p>

          {phase === "downgrade" && (
            <div className="space-y-2 rounded-lg border border-sky-200 bg-sky-50/70 p-3" data-testid="lp-policy-card">
              <div className="flex items-center gap-1.5 text-sm font-bold text-sky-900">
                <Send size={15} /> {t("wizard.lpPolicyTitle")}
              </div>
              <p className="text-xs text-sky-800">{t("wizard.lpPolicyDesc")}</p>
              {policyDone ? (
                <div className="space-y-2" data-testid="lp-policy-done">
                  <div className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 p-2 text-xs font-medium text-emerald-800">
                    <Check size={14} className="shrink-0" /> {existingPolicy
                      ? t("wizard.lpPolicyExists").replace("{scope}", existingPolicy.scopeName || existingPolicy.scopeSmtp || "—")
                      : t("wizard.lpPolicyCreated")}
                  </div>
                  <p className="text-xs text-zinc-600">{t("wizard.lpPolicyTestHint")}</p>
                  <div className="flex items-center gap-2">
                    <Button type="button" size="sm" variant="outline" onClick={sendTestEmail} disabled={testBusy} data-testid="lp-policy-test-btn" className="rounded-sm border-zinc-300">
                      {testBusy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Send size={14} className="mr-1.5" />}
                      {testBusy ? t("wizard.lpPolicyTestSending") : t("wizard.lpPolicyTestEmail")}
                    </Button>
                    {testSent && (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700" data-testid="lp-policy-test-sent">
                        <Check size={13} /> {t("wizard.lpPolicyTestSent")}
                      </span>
                    )}
                  </div>
                </div>
              ) : (scope === "loading" || scope === null) ? (
                <p className="text-xs text-zinc-500" data-testid="lp-policy-loading">{t("wizard.lpPolicyLoading")}</p>
              ) : (
                <>
                  {(!scope.mailboxes || scope.mailboxes.length === 0) && (
                    <p className="text-xs font-medium text-amber-700" data-testid="lp-policy-no-mailboxes">{t("wizard.lpPolicyNoMailboxes")}</p>
                  )}
                  {(!scope.groups || scope.groups.length === 0) && (
                    <p className="text-xs font-medium text-amber-700" data-testid="lp-policy-no-groups">{t("wizard.lpPolicyNoGroups")}</p>
                  )}
                  {scope.mailboxes?.length > 0 && scope.groups?.length > 0 && (
                    <div className="space-y-2">
                      <div>
                        <label className="text-xs font-semibold text-zinc-600">{t("wizard.lpPolicyMailbox")}</label>
                        <select value={selMbx} onChange={(e) => setSelMbx(e.target.value)} data-testid="lp-policy-mailbox"
                          className="mt-1 w-full rounded-sm border border-zinc-300 bg-white px-2 py-1.5 text-sm text-zinc-900">
                          {scope.mailboxes.map((m) => (
                            <option key={m.PrimarySmtpAddress} value={m.PrimarySmtpAddress}>{m.DisplayName} — {m.PrimarySmtpAddress}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-zinc-600">{t("wizard.lpPolicyGroup")}</label>
                        <select value={selGrp} onChange={(e) => setSelGrp(e.target.value)} data-testid="lp-policy-group"
                          className="mt-1 w-full rounded-sm border border-zinc-300 bg-white px-2 py-1.5 text-sm text-zinc-900">
                          {scope.groups.map((g) => (
                            <option key={g.PrimarySmtpAddress} value={g.PrimarySmtpAddress}>{g.DisplayName} — {g.PrimarySmtpAddress}</option>
                          ))}
                        </select>
                      </div>
                      <Button type="button" size="sm" onClick={createPolicy} disabled={policyBusy} data-testid="lp-policy-create-btn" className="rounded-sm">
                        {policyBusy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Send size={14} className="mr-1.5" />} {t("wizard.lpPolicyCreate")}
                      </Button>
                    </div>
                  )}
                </>
              )}
              <p className="text-[11px] text-zinc-500">{t("wizard.lpPolicySkip")}</p>
            </div>
          )}

          <ol className="space-y-2" data-testid="lp-steps">
            {steps.map((s, i) => (
              <li key={s} className="flex items-start gap-3 text-sm text-zinc-800">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-zinc-900 text-xs font-bold text-white">{i + 1}</span>
                <span className="pt-0.5">{t(s)}</span>
              </li>
            ))}
          </ol>

          <a href={ENTRA_ROLES_URL} target="_blank" rel="noreferrer" data-testid="lp-open-entra"
            className="inline-flex w-fit items-center gap-2 rounded-sm bg-primary px-3 py-2 text-sm font-bold text-white transition-colors hover:bg-[#002480]">
            <ExternalLink size={15} /> {t("wizard.lpOpenEntra")}
          </a>

          {appId && (
            <div className="rounded-md border border-zinc-200 bg-zinc-50 p-3" data-testid="lp-appid">
              <div className="text-xs font-semibold text-zinc-500">{t("wizard.lpAppLabel")}</div>
              <div className="mt-1 flex items-center gap-2">
                <code className="min-w-0 flex-1 truncate font-mono text-sm text-zinc-900" data-testid="lp-appid-value">{appId}</code>
                <Button type="button" variant="outline" size="sm" onClick={copyAppId} data-testid="lp-copy-btn" className="rounded-sm border-zinc-300">
                  {copied ? <Check size={14} className="mr-1.5 text-emerald-600" /> : <Copy size={14} className="mr-1.5" />} {t("wizard.lpCopy")}
                </Button>
              </div>
            </div>
          )}

          <div className="flex items-center gap-2">
            <Button type="button" variant="ghost" onClick={verify} disabled={busy} data-testid="lp-verify-btn">
              {busy ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <ShieldCheck size={15} className="mr-1.5" />} {t("wizard.lpVerify")}
            </Button>
            <span className="inline-flex items-center gap-1.5 text-xs text-zinc-400" data-testid="lp-checking">
              <Loader2 size={12} className="animate-spin" /> {t("wizard.lpChecking")}
            </span>
          </div>
        </>
      )}

      {phase === "complete" && (
        <div className="flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800" data-testid="lp-complete">
          <Mailbox size={16} className="mt-0.5 shrink-0" />
          <div>
            <div className="font-bold">{t("wizard.lpRoleRecipAdmin")}</div>
            <p className="mt-0.5 text-xs">{t("wizard.lpRecipientOk")}</p>
          </div>
        </div>
      )}
    </div>
  );
}
